// Lightweight, dependency-free helpers for client-side image downscaling/compression.

const MAX_DIMENSION = 2048; // px
const JPEG_QUALITY = 0.75;

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = (err) => {
			URL.revokeObjectURL(url);
			reject(err);
		};
		img.src = url;
	});
}

async function resizeAndEncodeImage(file: File): Promise<File> {
	const img = await loadImageFromFile(file);
	const scale = Math.min(
		MAX_DIMENSION / img.width,
		MAX_DIMENSION / img.height,
		1,
	);

	const targetWidth = Math.round(img.width * scale);
	const targetHeight = Math.round(img.height * scale);

	const canvas = document.createElement("canvas");
	canvas.width = targetWidth;
	canvas.height = targetHeight;
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		return file;
	}
	ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

	const mimeType = file.type === "image/png" ? "image/png" : "image/jpeg";

	const blob: Blob | null = await new Promise((resolve) =>
		canvas.toBlob(
			(b) => resolve(b),
			mimeType,
			mimeType === "image/png" ? undefined : JPEG_QUALITY,
		),
	);

	if (!blob) {
		return file;
	}

	// Preserve the original filename; browsers can open .png even if mime is jpeg.
	return new File([blob], file.name, { type: mimeType });
}

/**
 * Compresses images (downscale + encode) and leaves non-images untouched.
 */
export async function compressIfImage(file: File): Promise<File> {
	if (!file.type.startsWith("image/")) {
		return file;
	}
	try {
		return await resizeAndEncodeImage(file);
	} catch (_err) {
		return file;
	}
}

export function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}
